/**
 * NotificationListenerContext
 * ===========================
 * Single-mount wrapper around `useNotificationListener` so its side
 * effects (Realtime channels for `notification_deliveries` +
 * `notification_send_queue`, the SW postMessage bridge, toast emits, RTK
 * cache patches) run **once per session** instead of once per consumer.
 *
 * Why
 * ---
 * `NotificationBellIndicator` is rendered in both `Header.tsx` and
 * `Navigation.tsx`. Each instance previously called the listener hook
 * directly, so on any layout where both components render (i.e. most
 * authenticated routes) we opened **two** copies of the deliveries +
 * toast-queue Realtime channels with different `Math.random()` instance
 * IDs. That's 4 active subscriptions in `realtime.subscription` per
 * session, just for the bell. PR audit:
 * https://github.com/Flex-wise/Flexwise-front-end/pull/467 (replaced the
 * Math.random instance id with profileId at the same time).
 *
 * The provider hosts the hook once. Every bell instance reads
 * `lastPayloadRef` from this context — they no longer subscribe.
 *
 * Mount it just inside `<AuthProvider>` in `main.jsx` so it can call
 * `useAuth()`. Place it OUTSIDE the Router so a route change doesn't
 * remount the listener.
 */
import {
  createContext,
  useContext,
  type MutableRefObject,
  type ReactNode,
} from "react";
import { useNotificationListener } from "../hooks/useNotificationListener";
import type { PushPayload } from "../types/notificationTypes";

// ─── Context shape ────────────────────────────────────────────────────────────

interface NotificationListenerContextValue {
  /** Most recent push payload (mutated by SW + Realtime listeners). Bell
   *  consumers read this in a one-shot effect on mount to decide whether
   *  to open the detail dialog after a service-worker click. */
  lastPayloadRef: MutableRefObject<PushPayload | null>;
}

const NotificationListenerContext =
  createContext<NotificationListenerContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function NotificationListenerProvider({
  children,
}: {
  children: ReactNode;
}) {
  // Side effects (channels + SW handler + cache patching) live here. This
  // hook MUST run exactly once per session — that's the whole point.
  const { lastPayloadRef } = useNotificationListener();

  return (
    <NotificationListenerContext.Provider value={{ lastPayloadRef }}>
      {children}
    </NotificationListenerContext.Provider>
  );
}

// ─── Consumer hook ────────────────────────────────────────────────────────────

/**
 * Read the listener's `lastPayloadRef` from anywhere inside the
 * provider tree. Throws if the provider is missing — fail loud rather
 * than silently spawn a duplicate listener via a fallback.
 */
export function useNotificationListenerContext(): NotificationListenerContextValue {
  const ctx = useContext(NotificationListenerContext);
  if (!ctx) {
    throw new Error(
      "useNotificationListenerContext must be used inside <NotificationListenerProvider>",
    );
  }
  return ctx;
}
